import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/partner-terms")({
  head: () => ({
    meta: [
      { title: "Termos do Programa de Parceiros — Routify" },
      {
        name: "description",
        content:
          "Regras do Programa de Parceiros Routify: candidatura, códigos promocionais, comissões, uso da marca, antifraude e pagamentos.",
      },
    ],
  }),
  component: PartnerTermsPage,
});

function PartnerTermsPage() {
  return (
    <main className="mx-auto max-w-3xl px-6 py-16 text-foreground">
      <Link to="/legal" className="text-sm text-muted-foreground hover:text-foreground">← Legal</Link>
      <h1 className="mt-6 text-3xl font-semibold">Termos do Programa de Parceiros</h1>
      <p className="mt-2 text-xs text-muted-foreground">
        Última atualização: 2026-07-24 · Versão 1.0
      </p>

      <section className="mt-8 space-y-5 text-sm leading-relaxed">
        <p>
          Os presentes Termos regulam a participação de criadores de conteúdo, influenciadores e
          outros divulgadores ("Parceiro") no <strong>Programa de Parceiros da Routify</strong>{" "}
          (o "Programa"), operado pela entidade identificada na página{" "}
          <Link to="/legal" className="underline">Legal</Link>. A candidatura ao Programa através
          do formulário disponível em <Link to="/partner" className="underline">/partner</Link>{" "}
          implica a aceitação integral destes Termos, dos{" "}
          <Link to="/terms" className="underline">Termos e Condições</Link> gerais e da{" "}
          <Link to="/privacy" className="underline">Política de Privacidade</Link>.
        </p>

        <h2 className="text-lg font-semibold">1. Requisitos para participação</h2>
        <ul className="list-disc pl-5">
          <li>Ter pelo menos 18 anos e capacidade legal para celebrar contratos;</li>
          <li>Ser titular efetivo dos perfis/canais indicados na candidatura;</li>
          <li>Produzir conteúdo original, com atividade regular, em áreas compatíveis com o Serviço (viagens, turismo, gastronomia, lifestyle);</li>
          <li>Possuir comunidade real e envolvida, sem recurso a seguidores comprados ou tráfego artificial;</li>
          <li>Comunicar de forma compatível com a imagem da Routify, sem discursos de ódio, conteúdo ilícito ou lesivo de terceiros;</li>
          <li>Cumprir a legislação aplicável na sua jurisdição, incluindo obrigações fiscais decorrentes das comissões recebidas.</li>
        </ul>

        <h2 className="text-lg font-semibold">2. Processo de candidatura</h2>
        <ul className="list-disc pl-5">
          <li>A candidatura é submetida através do formulário público em <Link to="/partner" className="underline">/partner</Link>;</li>
          <li>O candidato deve prestar informações verdadeiras, completas e atualizadas;</li>
          <li>A Routify pode solicitar esclarecimentos, material adicional ou um período de avaliação antes de decidir;</li>
          <li>A submissão da candidatura não gera qualquer direito à aprovação nem ao pagamento de qualquer valor.</li>
        </ul>

        <h2 className="text-lg font-semibold">3. Critérios de aprovação</h2>
        <p>
          A aprovação é discricionária e baseada em critérios como: qualidade e originalidade do
          conteúdo, coerência editorial com a marca, dimensão e envolvimento real da comunidade,
          adequação da audiência ao público-alvo do Serviço, historial de parcerias anteriores e
          conformidade com estes Termos. O número de seguidores não garante, por si só,
          aprovação, nem existe um mínimo obrigatório.
        </p>

        <h2 className="text-lg font-semibold">4. Códigos promocionais e link de referência</h2>
        <ul className="list-disc pl-5">
          <li>Uma vez aprovado, o Parceiro recebe um <strong>código promocional exclusivo</strong> e um <strong>link de referência</strong> (<code>?ref=CODIGO</code>);</li>
          <li>O código concede aos seguidores do Parceiro um desconto na compra dos planos e produtos elegíveis (Premium, Pro, créditos e planeamento personalizado), nos termos definidos pela Routify;</li>
          <li>Os códigos são pessoais e intransmissíveis; não podem ser vendidos, leiloados ou combinados com outras promoções sem autorização expressa;</li>
          <li>A Routify pode alterar, suspender ou substituir códigos por motivos operacionais, de segurança ou por alteração dos preços;</li>
          <li>O desconto aplica-se apenas a compras concluídas com sucesso e que não sejam posteriormente objeto de reembolso, disputa (chargeback) ou cancelamento.</li>
        </ul>

        <h2 className="text-lg font-semibold">5. Comissões</h2>
        <ul className="list-disc pl-5">
          <li>O Parceiro tem direito a uma comissão calculada sobre o <strong>valor líquido efetivamente recebido pela Routify</strong> (após deduzir IVA, taxas Stripe, reembolsos e chargebacks) das compras validamente atribuídas ao seu código ou link;</li>
          <li>A percentagem de comissão é comunicada individualmente na aprovação e pode ser revista por acordo entre as partes ou por alteração unilateral com aviso prévio de 30 dias;</li>
          <li>A atribuição de uma venda ao Parceiro depende (i) da aplicação do código promocional na compra ou (ii) do registo do parâmetro <code>ref</code> no dispositivo do utilizador, válido por <strong>30 dias a partir do clique mais recente</strong> nesse link — cada novo clique no mesmo link renova a janela por mais 30 dias; passado esse prazo sem novo clique, a atribuição expira e a compra deixa de gerar comissão;</li>
          <li>Não são elegíveis para comissão: compras efetuadas pelo próprio Parceiro, por familiares diretos, contas de teste, compras reembolsadas, disputadas ou fraudulentas, bem como acessos concedidos gratuitamente pela Routify;</li>
          <li>As comissões acumuladas ficam visíveis no painel do Parceiro em <Link to="/affiliate" className="underline">/affiliate</Link>.</li>
        </ul>

        <h2 className="text-lg font-semibold">6. Utilização da marca Routify</h2>
        <ul className="list-disc pl-5">
          <li>É concedida ao Parceiro uma licença limitada, não exclusiva, revogável e intransmissível para utilizar o nome, logótipo e materiais oficiais da Routify na divulgação do Serviço, no âmbito estrito do Programa;</li>
          <li>O Parceiro deve utilizar apenas versões oficiais do logótipo e respeitar as regras de composição, espaçamento e cores fornecidas no kit de parceiro;</li>
          <li>Não é permitido: alterar o logótipo, sugerir uma relação institucional, laboral ou societária inexistente com a Routify, registar domínios, marcas ou perfis com o nome "Routify" ou variações confundíveis, nem licitar sobre a palavra-chave "Routify" e variações em plataformas de anúncios sem autorização prévia por escrito;</li>
          <li>A comunicação deve identificar claramente a natureza comercial ("parceria", "publicidade", "#ad" ou equivalente na jurisdição do Parceiro), em cumprimento das regras de publicidade aplicáveis;</li>
          <li>Todos os direitos sobre a marca, software e conteúdos originais da Routify permanecem exclusivamente da Routify.</li>
        </ul>

        <h2 className="text-lg font-semibold">7. Suspensão e cessação da parceria</h2>
        <p>A Routify pode suspender ou terminar a parceria, com efeitos imediatos e sem direito a indemnização, nomeadamente quando:</p>
        <ul className="list-disc pl-5">
          <li>Se verifique violação destes Termos, dos Termos gerais ou da lei aplicável;</li>
          <li>Se detete fraude, abuso, manipulação de tráfego, autocompra, uso de bots ou tráfego incentivado ("cashback", "coupon sites" não autorizados);</li>
          <li>O Parceiro publique conteúdo ilícito, difamatório, discriminatório ou que prejudique a reputação da Routify;</li>
          <li>O Parceiro deixe de cumprir os requisitos previstos na cláusula 1;</li>
          <li>O Parceiro utilize a marca Routify em violação da cláusula 6;</li>
          <li>Existam indícios sérios de utilização indevida de dados pessoais dos seguidores.</li>
        </ul>
        <p>
          Qualquer das partes pode ainda terminar a parceria, sem invocação de justa causa,
          mediante comunicação por email com 30 dias de antecedência. Cessada a parceria, o código
          e o link deixam de gerar comissões; as comissões devidas até essa data e não afetadas
          por fraude ou reembolso serão liquidadas nos termos da cláusula 10.
        </p>

        <h2 className="text-lg font-semibold">8. Responsabilidades do Parceiro</h2>
        <ul className="list-disc pl-5">
          <li>Responder pela veracidade das informações prestadas na candidatura e no perfil;</li>
          <li>Garantir que o conteúdo publicado cumpre a legislação aplicável, incluindo regras de publicidade, defesa do consumidor, propriedade intelectual e proteção de dados;</li>
          <li>Não fazer declarações enganosas sobre o Serviço, funcionalidades, preços, resultados ou vantagens;</li>
          <li>Cumprir as suas obrigações fiscais e contributivas relativamente às comissões auferidas, incluindo emissão de fatura/recibo válido quando exigido pela lei aplicável;</li>
          <li>Indemnizar a Routify por quaisquer danos, custos ou reclamações de terceiros resultantes do incumprimento destas obrigações.</li>
        </ul>

        <h2 className="text-lg font-semibold">9. Política antifraude</h2>
        <ul className="list-disc pl-5">
          <li>São expressamente proibidas quaisquer práticas destinadas a gerar comissões artificialmente, incluindo autocompra, uso de identidades falsas, contas descartáveis, VPN/proxy para simular utilizadores distintos, cliques ou registos incentivados, bots, scripts ou tráfego não humano;</li>
          <li>A Routify utiliza mecanismos de deteção de fraude (análise de padrões de compra, IP, dispositivos, taxa de reembolso, chargebacks e sobreposição de utilizadores) e pode reter cautelarmente comissões suspeitas enquanto investiga;</li>
          <li>Confirmada a fraude, a Routify pode: anular as comissões afetadas, exigir a devolução de valores já pagos, terminar imediatamente a parceria e, em casos graves, participar às autoridades competentes;</li>
          <li>O Parceiro compromete-se a colaborar com a Routify no esclarecimento de quaisquer situações suspeitas.</li>
        </ul>

        <h2 className="text-lg font-semibold">10. Pagamento das comissões</h2>
        <ul className="list-disc pl-5">
          <li>As comissões são apuradas mensalmente, com base nas vendas efetivas ocorridas no mês anterior, deduzidas de reembolsos e chargebacks;</li>
          <li>O pagamento é efetuado por transferência bancária (SEPA) ou outro meio previamente acordado, para a conta indicada pelo Parceiro;</li>
          <li>O pagamento fica condicionado a: (i) saldo mínimo elegível de <strong>50 €</strong>; se o saldo for inferior, transita para o mês seguinte; (ii) inexistência de indícios de fraude ou disputa em análise; (iii) entrega dos documentos fiscais aplicáveis (fatura/recibo);</li>
          <li>Os pagamentos são efetuados até ao dia 15 do mês seguinte ao apuramento, salvo circunstâncias justificadas comunicadas ao Parceiro;</li>
          <li>Eventuais taxas bancárias, cambiais ou fiscais fora de Portugal ficam a cargo do Parceiro, salvo acordo em contrário;</li>
          <li>Reembolsos ou chargebacks ocorridos após o pagamento podem ser deduzidos em apuramentos futuros.</li>
        </ul>

        <h2 className="text-lg font-semibold">11. Proteção de dados</h2>
        <p>
          Os dados pessoais recolhidos no formulário de candidatura e na gestão da parceria são
          tratados nos termos da <Link to="/privacy" className="underline">Política de Privacidade</Link>.
          O Parceiro é responsável, perante os seus seguidores, pelo tratamento de dados que
          eventualmente realize nas suas próprias plataformas.
        </p>

        <h2 className="text-lg font-semibold">12. Alterações aos Termos</h2>
        <p>
          A Routify pode atualizar estes Termos. Alterações materiais serão comunicadas por
          email com pelo menos 30 dias de antecedência. A continuação da utilização do código
          ou link após a entrada em vigor constitui aceitação das novas condições.
        </p>

        <h2 className="text-lg font-semibold">13. Lei aplicável e jurisdição</h2>
        <p>
          Estes Termos regem-se pela lei portuguesa. Para qualquer litígio, são competentes os
          tribunais portugueses, sem prejuízo dos direitos imperativos do consumidor e das
          entidades de resolução alternativa de litígios indicadas na página{" "}
          <Link to="/legal" className="underline">Legal</Link>.
        </p>
      </section>
    </main>
  );
}
